import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { RequestCancelAxios } from 'shared/api/lib'
import { ConversationsApi } from 'entities/Conversation'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ConversationMessagesStore } from 'widgets/ConversationMessages'
import { ConversationNewSearchStore } from 'widgets/ConversationNew'
import type { ContactsDetailsStore } from 'widgets/ContactsDetails'
import { OpenConversationAction } from 'widgets/ContactsDetails/ui/ContactsConversationOpen/ui/OpenConversationAction'

export class ContactsConversationOpenStore {
  private readonly _conversationNewSearchStore: ConversationNewSearchStore
  private readonly _conversationMessagesStore: ConversationMessagesStore
  private _contactId: number | null = null
  private _inboxId: number | null = null
  private _disposeInit: IReactionDisposer | null = null
  private _disposeContact: IReactionDisposer | null = null
  private _disposeConversation: IReactionDisposer | null = null
  private _cancelRequestSearchConversation = new RequestCancelAxios()
  private _cancelRequestCreateConversation = new RequestCancelAxios()
  private _loadingMessages = false

  constructor(
    private _contactsDetailsStore: ContactsDetailsStore,
    private _entities: IEntitiesStore
  ) {
    this._conversationMessagesStore = new ConversationMessagesStore(this._entities)
    this._conversationNewSearchStore = new ConversationNewSearchStore(this._entities, {
      autoSelectInbox: false,
      personalInboxesOnly: true,
      includeRCS: true,
      getDropdownItemsOptions: {
        showOnlyVerifiedToolFree: true,
      },
    })

    this._conversationNewSearchStore.setConfig({
      headerActions: <OpenConversationAction onClick={this.openNewConversation} />,
      readOnly: true,
      autoSelectInbox: false,
      changeInbox: async (inbox) => {
        if (!inbox.id) return
        if (this._inboxId === inbox.id) return

        this.setInboxId(inbox.id)
        await this.initConversation(inbox.id)
      },
    })

    this._conversationMessagesStore.setConfig({
      onStartSendMessage: this.createConversation,
      onCreateConversation: async (scheduledData, isDoubleOptInButton) => {
        await this._conversationNewSearchStore.handleCreateConversation(
          scheduledData,
          isDoubleOptInButton
        )
      },
      onCheckSendAction: () => {
        return this._conversationNewSearchStore.checkError()
      },
    })

    makeAutoObservable(this)
  }

  get conversationStore() {
    return this._entities.conversationStore
  }

  setSubSource = (value: string) => {
    this._conversationMessagesStore.messageFieldStore.setSubSource(value)
  }

  init = () => {
    this.initReaction()
    this.conversationMessagesStore.setSendFromStore(this._conversationNewSearchStore.sendFromStore)
  }

  initReaction = () => {
    this._conversationMessagesStore.initReactions()
    this.reactionInit()
    this.reactionContact()
    this.reactionConversation()
  }

  initConversation = async (inboxId: number) => {
    if (!inboxId) return
    if (!this._contactId) return
    if (this._loadingMessages) return

    try {
      runInAction(() => {
        this._loadingMessages = true
      })

      this._conversationMessagesStore.setHasPrevious(false)
      this._conversationMessagesStore.resetMessage()
      this._conversationMessagesStore.messageFieldStore.setDisabledMessageField(true)
      this._conversationMessagesStore.setLoading(true)

      this._cancelRequestSearchConversation.init()

      const conversationResponse = await ConversationsApi.getConversationsSearch(
        {
          teams: [inboxId],
          contacts: [this._contactId],
          page: 1,
        },
        {
          cancelToken: this._cancelRequestSearchConversation.token,
        }
      ).then((data) => data.data.results[0])

      this._conversationMessagesStore.setLoading(true)

      if (conversationResponse) {
        const conversation = this.conversationStore.addItem(conversationResponse)

        this.setConversationId(conversation.id)
        this._contactsDetailsStore.setConversationId(conversation.id)

        const inbox = this._entities.inboxesStore.getItemShared(inboxId)

        if (inbox && inbox.isSmartInbox && conversationResponse?.number_id) {
          this._conversationNewSearchStore.handleSetActiveNumber(
            inboxId,
            conversationResponse.number_id
          )
        }
      } else {
        const inbox = this._entities.inboxesStore.getItemShared(inboxId)

        if (inbox) {
          const numbersCount = inbox.numberIds.length
          if (numbersCount > 1) {
            this._conversationNewSearchStore.handleSetActiveNumber(
              inboxId,
              this._conversationNewSearchStore.activeNumberId,
              this._conversationNewSearchStore.smartOption
            )
          }
        }

        this._contactsDetailsStore.setConversationId(null)
      }

      this._conversationMessagesStore.messageFieldStore.setDisabledMessageField(false)

      if (conversationResponse?.id) {
        this._conversationMessagesStore.setLoading(true)
        await this._conversationMessagesStore.loadMessages()
      } else {
        await this._conversationMessagesStore.loadMessagesContact({
          before: this._conversationMessagesStore.loadingBeforeId,
        })
      }

      this._conversationMessagesStore.setScrollBottomTrigger()
    } catch (e) {
      console.log(e)
    } finally {
      this._conversationMessagesStore.messageFieldStore.setDisabledMessageField(false)

      runInAction(() => {
        this._loadingMessages = false
      })
    }
  }

  reactionConversation = () => {
    this._disposeConversation?.()
    this._disposeConversation = reaction(
      () => this._contactsDetailsStore.conversation?.id,
      (id) => {
        if (id) {
          this._contactsDetailsStore.setConversationId(id)
          this._conversationMessagesStore.setConversationId(id, false)
        } else {
          this._contactsDetailsStore.setConversationId(null)
          this._conversationMessagesStore.resetConversationId()
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionInit = () => {
    this._disposeInit?.()
    this._disposeInit = reaction(
      () => this._contactsDetailsStore.contact?.id,
      (contactId) => {
        if (!contactId) return

        const contact = this._entities.contactsStore.getItem(contactId)

        if (contact) {
          this.setContactId(contact.id)

          if (this._inboxId) {
            this.initConversation(this._inboxId)
          } else {
            this.setCurrentInbox()
          }
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  setCurrentInbox = () => {
    const inbox = this._entities.inboxesStore.currentInbox

    if (inbox?.type === 'inbox') {
      this.setInboxId(inbox.id)
      this.initConversation(inbox.id)

      const inboxId = inbox.id
      this._contactsDetailsStore.setInboxId(inboxId)
    } else {
      this._conversationMessagesStore.setLoading(false)
    }
  }

  handleInbox = async (inboxId: number) => {
    const inbox = this._entities.inboxesStore.getItemShared(inboxId)
    const { activeInboxId, activeNumberId, handleSetActiveNumber, smartOption } =
      this._conversationNewSearchStore

    if (inbox) {
      const inboxId = inbox.id
      this._contactsDetailsStore.setInboxId(inbox.id)

      let numberId = inbox.numberId

      if (activeInboxId === inboxId && typeof activeNumberId === 'number') {
        numberId = activeNumberId
      }

      const smart_option = activeInboxId === inboxId ? smartOption : undefined

      handleSetActiveNumber(inboxId, numberId, smart_option)
      this._conversationMessagesStore.setInboxId(inbox.id)
    }
  }

  reactionContact = () => {
    this._disposeContact?.()
    this._disposeContact = reaction(
      () => this._contactId,
      (value) => {
        if (!value) return

        const contact = this._entities.contactsStore.getItem(value)

        if (contact) {
          this._conversationNewSearchStore.handleAddItem(contact)
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  setContactId = (id: number) => {
    this._contactId = id
    this._conversationMessagesStore.setContactId(id)
  }

  setInboxId = (id: number) => {
    this._inboxId = id
    this.handleInbox(id)
  }

  open = (inboxId?: number) => {
    this._contactsDetailsStore.contactsDetailsGlobalStore?.handleChangeCollapse(false)
    this._contactsDetailsStore.contactsDetailsGlobalStore?.setOverlay(true)

    if (inboxId) this.setInboxId(inboxId)
  }

  createConversation = async () => {
    if (this._conversationMessagesStore.conversationId) return
    if (!this._contactId) return
    if (!this._inboxId) return

    try {
      this._cancelRequestCreateConversation.init()

      this._conversationMessagesStore.messageFieldStore.setLoading(true)

      const conversationResponse = await ConversationsApi.create(
        {
          contact_id: this._contactId,
          team_id: this._inboxId,
          number_id: this._conversationNewSearchStore.activeNumberId,
          smart_option: this._conversationNewSearchStore.smartOption ?? undefined,
        },
        {
          cancelToken: this._cancelRequestCreateConversation.token,
        }
      ).then((data) => data.data)

      const conversation = this.conversationStore.addItem(conversationResponse)
      this._contactsDetailsStore.setConversationId(conversation.id)

      if (conversationResponse?.number_id) {
        const inbox = this._entities.inboxesStore.getItem(this._inboxId)

        if (inbox && inbox.type === 'inbox' && inbox.isSmartInbox) {
          this._conversationNewSearchStore.handleSetActiveNumber(
            inbox?.id,
            conversationResponse.number_id
          )
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  setConversationId = (conversationId: number) => {
    this._conversationMessagesStore.setConversationId(conversationId, true)
  }
  setMessageId = (id: number) => {
    this._conversationMessagesStore.setMessageId(id)
  }

  openNewConversation = async () => {
    if (!this._contactId) return

    if (this.conversation) {
      await this.conversationStore.conversationOpenStore.setSignal({
        type: 'open_exist_conversation',
        payload: this.conversation.origin,
      })
    } else {
      const contact = this._entities.contactsStore.getItem(this._contactId)
      const inbox = this._entities.inboxesStore.getItem(this._inboxId)

      if (inbox) this._contactsDetailsStore.setInboxId(inbox.id)
      const numberId = this._conversationNewSearchStore.sendFromStore.number_id

      if (contact) {
        await this.conversationStore.conversationOpenStore.setSignal({
          type: 'open_new_conversation',
          payload: {
            contact: contact,
            inbox: inbox?.type === 'inbox' ? inbox : null,
            numberId,
          },
        })
      }
    }
    this._contactsDetailsStore.contactsDetailsGlobalStore?.handleChangeCollapse(false)
    this._contactsDetailsStore.contactsDetailsGlobalStore?.setOverlay(false)
    this._contactsDetailsStore.config?.emitOpenConversationSignal?.()
    this._contactsDetailsStore.config?.onDeleteContactsDetails?.()
  }

  reset = () => {
    this._conversationMessagesStore.resetReactions()
    this._conversationMessagesStore.resetConversationId()
    this._conversationMessagesStore.resetVariant()
    this._contactId = null
    this._disposeInit?.()
    this._disposeContact?.()
    this._disposeConversation?.()
    this._cancelRequestSearchConversation.reset()
    this._cancelRequestCreateConversation.reset()
    this._conversationMessagesStore.setSendFromStore(null)
  }

  get conversationNewSearchStore() {
    return this._conversationNewSearchStore
  }

  get conversationMessagesStore() {
    return this._conversationMessagesStore
  }

  get contactsDetailsStore() {
    return this._contactsDetailsStore
  }

  get inbox() {
    return this._conversationNewSearchStore.sendFromStore.inbox
  }

  get conversation() {
    return this._contactsDetailsStore.conversation
  }

  get contact() {
    if (this._contactId === null) return

    return this._entities.contactsStore.getItem(this._contactId)
  }

  get loading() {
    return this._contactsDetailsStore.loading
  }
}
